import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';

export interface BillingTransaction {
  id: number;
  product_id: number | null;
  pd_session_code: string | null;
  amount: number;
  currency: string;
  status: string;
  occurred_at: string;
  product: { name: string; slug: string } | null;
}

export interface BillingSummary {
  pending_total: number;
  pending_count: number;
  /** PREPAID | POSTPAID — the tenant's commercial model. */
  billing_type: string | null;
  /** DAILY | WEEKLY | MONTHLY; only set for POSTPAID tenants. */
  billing_frequency: string | null;
}

export interface InvoiceRecord {
  id: number;
  invoice_number: string;
  period_start: string;
  period_end: string;
  subtotal: number;
  gst_percent: number | null;
  gst_amount: number;
  total: number;
  currency: string;
  status: string;
  paid_amount: number;
  payment_status: 'PENDING' | 'PARTIALLY_PAID' | 'PAID';
  balance: number;
  sent_at: string | null;
  paid_at: string | null;
  created_at: string;
}

export interface PaymentHistoryRow {
  id: number;
  paid_date: string | null;
  method: string;
  offline_method?: string | null;
  amount: number;
  tds_amount: number;
  /** Amount + TDS — same credit as invoice Paid. Absent on an older API. */
  total?: number;
  status: 'PENDING_VERIFICATION' | 'PAID' | 'REJECTED' | 'FAILED';
  rejection_reason: string | null;
}

/** One payment across any invoice, for the Payment Transactions page. */
export interface PaymentTransactionRow {
  id: number;
  created_at: string;
  paid_date: string | null;
  method: string;
  provider: string | null;
  offline_method: string | null;
  status: string;
  amount: number;
  tds_amount: number;
  currency: string;
  reference: string | null;
  provider_payment_id: string | null;
  rejection_reason: string | null;
  has_receipt: boolean;
  invoice: {
    id: number;
    invoice_number: string | null;
    invoice_type: string | null;
    total: number;
    plan_name: string | null;
  } | null;
}

export interface PaymentTransactionQuery {
  status?: string;
  method?: string;
  type?: string;
  search?: string;
  from?: string;
  to?: string;
  page?: number;
  page_size?: number;
}

export interface SubscriptionPlan {
  id: number;
  uuid?: string | null;
  subscription_name: string | null;
  subscription_type: string | null;
  subscription_price: number | null;
  validity_days: number | null;
  no_of_subscription_models?: Record<string, unknown> | null;
  is_active?: boolean | null;
}

/** One model's allowance and what has been drawn from it since the plan started. */
export interface ModelQuotaUsage {
  product_id: number;
  product_name: string;
  count: number;
  used: number;
  remaining: number;
  /** Sessions run past the plan allowance; billed separately in arrears. */
  overage?: number;
}

export interface CurrentSubscriptionEntitlement {
  subscription_tenant_id: number;
  status: string;
  starts_at: string | null;
  ends_at: string | null;
  days_remaining: number;
  subscription: SubscriptionPlan;
  /** Counted server-side from the usage ledger; absent on an older API. */
  model_quotas?: ModelQuotaUsage[];
  /** Plan already paid for that takes over when this one ends. */
  upcoming?: UpcomingSubscription | null;
  /** Invoice this plan was bought on; absent for entitlements granted without a purchase. */
  invoice_id?: number | null;
}

export interface UpcomingSubscription {
  subscription_tenant_id: number;
  status: string;
  starts_at: string | null;
  ends_at: string | null;
  starts_in_days: number | null;
  invoice_id: number | null;
  subscription: SubscriptionPlan;
}

export interface SubscriptionCheckoutPayload {
  invoice: { id: number; invoice_number?: string };
  checkout: {
    publicKey: string;
    providerOrderId: string;
    amountMinor: number;
    currency: string;
    invoiceNumber: string;
    invoiceId?: number;
    prefill?: unknown;
  };
}

@Injectable({ providedIn: 'root' })
export class BillingApiService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiBaseUrl.replace(/\/$/, '');

  summary() {
    return this.http.get<{ data: BillingSummary }>(`${this.base}/billing/summary`);
  }

  transactions(page = 1, pageSize = 50) {
    return this.http.get<{ data: { items: BillingTransaction[]; total: number } }>(
      `${this.base}/billing/transactions?page=${page}&page_size=${pageSize}`,
    );
  }

  invoices() {
    return this.http.get<{ data: { items: InvoiceRecord[]; total: number } }>(`${this.base}/billing/invoices`);
  }

  downloadInvoicePdf(id: number) {
    return this.http.get(`${this.base}/billing/invoices/${id}/pdf`, { responseType: 'blob' });
  }

  // Omitting the amount pays the full outstanding balance.
  createOrder(id: number, amount?: number) {
    return this.http.post<{ data: any }>(
      `${this.base}/billing/invoices/${id}/pay`,
      amount == null ? {} : { amount },
    );
  }

  /** All payments for this tenant — searchable, filterable, paginated. */
  listPayments(query: PaymentTransactionQuery) {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && `${value}` !== '') {
        params = params.set(key, String(value));
      }
    }
    return this.http.get<{
      data: { items: PaymentTransactionRow[]; total: number; page: number; page_size: number };
    }>(`${this.base}/billing/payments`, { params });
  }

  paymentHistory(id: number) {
    return this.http.get<{ data: { items: PaymentHistoryRow[] } }>(
      `${this.base}/billing/invoices/${id}/payments`,
    );
  }

  // Verify the Razorpay payment signature after checkout completes.
  verifyPayment(id: number, body: { providerOrderId: string; providerPaymentId: string; signature: string }) {
    return this.http.post<{ data: any }>(`${this.base}/billing/invoices/${id}/pay/verify`, body);
  }

  // Record an offline payment (NEFT/RTGS/cheque/cash/UPI) with an optional receipt file.
  recordOffline(id: number, form: FormData) {
    return this.http.post<{ data: any }>(`${this.base}/billing/invoices/${id}/pay/offline`, form);
  }

  listSubscriptions() {
    return this.http.get<{ data: SubscriptionPlan[] }>(`${this.base}/billing/subscriptions`);
  }

  currentSubscription() {
    return this.http.get<{ data: CurrentSubscriptionEntitlement | null }>(
      `${this.base}/billing/subscriptions/current`,
    );
  }

  purchaseSubscription(subscriptionId: number) {
    return this.http.post<{ data: SubscriptionCheckoutPayload }>(
      `${this.base}/billing/subscriptions/purchase`,
      { subscription_id: subscriptionId },
    );
  }

}
